import { BookOpen, Clock3, FileText, ListVideo } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  fetchCourseTranscript,
  findActiveTranscriptSegmentIndex,
  findActiveTranscriptWordIndex,
  getTimedTranscriptWords,
  type CourseTranscript,
  type TranscriptSegment,
} from "@/lib/courses/courseTranscript";
import type { CourseChapter } from "@/types/course";

type LessonResourcesProps = {
  description?: string | null;
  chapters: CourseChapter[];
  transcriptUrl?: string | null;
  currentTime?: number;
  onSeek?: (seconds: number) => void;
};

function formatTimestamp(value: number): string {
  const totalSeconds = Math.max(0, Math.floor(value));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  }

  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

function HighlightedTranscriptSegment({
  segment,
  currentTime,
}: {
  segment: TranscriptSegment;
  currentTime: number;
}) {
  const words = getTimedTranscriptWords(segment);
  const activeWordIndex = findActiveTranscriptWordIndex(words, currentTime);

  return words.map((word, wordIndex) => {
    const isActiveWord = wordIndex === activeWordIndex;
    return (
      <span
        key={`${word.start}-${wordIndex}`}
        data-active-word={isActiveWord ? "true" : undefined}
        className={
          isActiveWord
            ? "rounded bg-primary px-0.5 font-medium text-primary-foreground"
            : wordIndex < activeWordIndex
              ? "text-foreground/90"
              : "text-muted-foreground"
        }
      >
        {word.text}
        {wordIndex < words.length - 1 ? " " : ""}
      </span>
    );
  });
}

export function LessonResources({
  description,
  chapters,
  transcriptUrl,
  currentTime = 0,
  onSeek,
}: LessonResourcesProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("overview");
  const [transcript, setTranscript] = useState<CourseTranscript | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const previousTranscriptUrlRef = useRef(transcriptUrl);
  const activeSegmentRef = useRef<HTMLButtonElement>(null);
  const activeSegmentIndex = transcript
    ? findActiveTranscriptSegmentIndex(transcript.segments, currentTime)
    : -1;

  useEffect(() => {
    if (previousTranscriptUrlRef.current === transcriptUrl) return;
    previousTranscriptUrlRef.current = transcriptUrl;
    setActiveTab("overview");
    setTranscript(null);
    setIsLoading(false);
    setHasError(false);
  }, [transcriptUrl]);

  useEffect(() => {
    if (
      activeTab !== "transcript" ||
      !transcriptUrl ||
      transcript
    ) {
      return;
    }

    const controller = new AbortController();
    setIsLoading(true);
    setHasError(false);

    fetchCourseTranscript(transcriptUrl, controller.signal)
      .then(setTranscript)
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") return;
        setHasError(true);
      })
      .finally(() => setIsLoading(false));

    return () => controller.abort();
  }, [activeTab, transcript, transcriptUrl]);

  useEffect(() => {
    if (activeTab !== "transcript" || activeSegmentIndex < 0) return;
    activeSegmentRef.current?.scrollIntoView?.({
      behavior: "smooth",
      block: "nearest",
    });
  }, [activeSegmentIndex, activeTab]);

  return (
    <section className="overflow-hidden rounded-2xl border border-border/80 bg-card/70 shadow-sm backdrop-blur-sm">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col gap-3 border-b border-border/70 bg-gradient-to-r from-primary/[0.08] via-transparent to-[hsl(var(--brand-secondary)/0.07)] p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <div className="grid h-9 w-9 place-items-center rounded-xl border border-primary/20 bg-primary/10 text-primary">
              <BookOpen className="h-4 w-4" />
            </div>
            <div>
              <h2 className="text-sm font-semibold">
                {t("course.lessonResources.title")}
              </h2>
              <p className="text-xs text-muted-foreground">
                {t("course.lessonResources.subtitle")}
              </p>
            </div>
          </div>

          <TabsList className="h-9 w-full bg-background/70 p-1 sm:w-auto">
            <TabsTrigger
              value="overview"
              className="h-7 flex-1 gap-1.5 px-3 text-xs data-[state=active]:text-primary sm:flex-none"
            >
              <ListVideo className="h-3.5 w-3.5" />
              {t("course.lessonResources.overviewTab")}
            </TabsTrigger>
            <TabsTrigger
              value="transcript"
              disabled={!transcriptUrl}
              className="h-7 flex-1 gap-1.5 px-3 text-xs data-[state=active]:text-primary sm:flex-none"
            >
              <FileText className="h-3.5 w-3.5" />
              {t("course.lessonResources.transcriptTab")}
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="overview" className="m-0 p-4 sm:p-5">
          {description && (
            <p className="max-w-3xl text-sm leading-6 text-muted-foreground">
              {description}
            </p>
          )}

          {chapters.length > 0 && (
            <div className={description ? "mt-5" : undefined}>
              <div className="mb-2.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                <Clock3 className="h-3.5 w-3.5 text-primary" />
                {t("course.lessonResources.chapters")}
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                {chapters.map((chapter) => (
                  <button
                    key={`${chapter.startSeconds}-${chapter.title}`}
                    type="button"
                    disabled={!onSeek}
                    onClick={() => onSeek?.(chapter.startSeconds)}
                    className="group flex items-center gap-3 rounded-xl border border-border/70 bg-background/60 px-3 py-2.5 text-left transition hover:border-primary/30 hover:bg-primary/[0.04] disabled:cursor-default disabled:hover:border-border/70 disabled:hover:bg-background/60"
                  >
                    <span className="min-w-[48px] rounded-md bg-primary/10 px-1.5 py-1 text-center text-[11px] font-semibold tabular-nums text-primary">
                      {formatTimestamp(chapter.startSeconds)}
                    </span>
                    <span className="text-sm font-medium leading-snug text-foreground/85 group-hover:text-foreground">
                      {chapter.title}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="transcript" className="m-0">
          {isLoading && (
            <div className="space-y-3 p-5" aria-live="polite">
              <p className="text-sm text-muted-foreground">
                {t("course.lessonResources.loading")}
              </p>
              {[0, 1, 2, 3].map((item) => (
                <div
                  key={item}
                  className="h-10 animate-pulse rounded-lg bg-muted"
                />
              ))}
            </div>
          )}

          {hasError && (
            <div className="p-5 text-sm text-destructive" role="alert">
              {t("course.lessonResources.error")}
            </div>
          )}

          {transcript && (
            <div className="max-h-[420px] overflow-y-auto p-2 sm:p-3">
              {transcript.segments.map((segment, index) => (
                <button
                  key={`${segment.start}-${index}`}
                  ref={index === activeSegmentIndex ? activeSegmentRef : null}
                  type="button"
                  disabled={!onSeek}
                  onClick={() => onSeek?.(segment.start)}
                  className={`group flex w-full items-start gap-3 rounded-xl border px-2.5 py-2.5 text-left transition ${
                    index === activeSegmentIndex
                      ? "border-primary/20 bg-primary/[0.07]"
                      : "border-transparent hover:bg-primary/[0.05]"
                  } disabled:cursor-text`}
                >
                  <span className="mt-0.5 min-w-[48px] text-xs font-semibold tabular-nums text-primary">
                    {formatTimestamp(segment.start)}
                  </span>
                  <span className="text-sm leading-6 text-muted-foreground transition group-hover:text-foreground">
                    {index === activeSegmentIndex
                      ? (
                          <HighlightedTranscriptSegment
                            segment={segment}
                            currentTime={currentTime}
                          />
                        )
                      : segment.text}
                  </span>
                </button>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </section>
  );
}
